import {MapProvider} from '../../enums/map-provider.enum';
import {MapType} from '../../enums/map-type';

/** Region key for providers whose imagery covers the whole world. */
export const GLOBAL_REGION = 'global';

/** What a layer actually shows, so the picker can say so under the name. */
export enum MapKind {
  AERIAL = 'map_kind_aerial',
  STREET = 'map_kind_street',
  TOPOGRAPHIC = 'map_kind_topographic',
}

export interface LayerDef {
  /**
   * Tile URL with {z} {x} {y} placeholders.
   *
   * The order of the placeholders in the string is the whole story: there is
   * no separate row/column flag, because a service that wants y before x just
   * says so here. Every template below was checked against a real tile inside
   * its own country, and against the same tile with x and y swapped -- the
   * swap has to 404 or come back blank, otherwise the order is wrong and the
   * map quietly shows the wrong place.
   */
  url: string;

  /**
   * Last zoom the service actually has tiles for, found by walking up until
   * the response turned into a 404 or a blank ~1KB tile. Past this the base
   * provider upscales the parent tile, so being a level conservative costs
   * sharpness while being a level optimistic shows holes.
   */
  maxZoom: number;
}

export interface MapProviderDef {
  id: MapProvider;

  /** A proper noun. Not translated. */
  name: string;

  /** ISO 3166-1 alpha-2, or GLOBAL_REGION. Groups the picker. */
  region: string;

  /** Flag for the section heading. Empty for global providers. */
  flag: string;

  /**
   * Required by every licence here -- these services are free for commercial
   * use *on condition* the source is credited, so the credit is part of using
   * them rather than decoration.
   */
  attribution: string;

  /**
   * One layer per map type. A type with no entry of its own falls back to
   * `default`, which is why a provider publishing only orthophotos still
   * answers for every position of the map-type toggle.
   */
  layers: {[mapType: string]: LayerDef};

  /** What `default` shows, for the subtitle in the picker. */
  kind: MapKind;

  /** Whether the provider has aerial imagery of its own under SATELLITE. */
  hasAerial: boolean;
}

/**
 * Providers that are nothing but a URL template.
 *
 * Anything needing a key, a signature or a quadkey (Bing, Mapbox, HERE,
 * TomTom, Google) stays a hand-written class; those earn their file. A plain
 * national WMTS does not, and one class per country is how that list grew.
 *
 * Licences: every entry is open data that permits commercial use with
 * attribution. Services restricting commercial use, or leaving it ambiguous,
 * are deliberately absent.
 */
export const MAP_PROVIDER_DEFS: MapProviderDef[] = [

  // -- Spain -----------------------------------------------------------
  // Condiciones generales de uso IGN: free reuse, commercial included,
  // with attribution.
  {
    id: MapProvider.IGN_ES,
    name: 'IGN España',
    region: 'ES',
    flag: '\u{1F1EA}\u{1F1F8}',
    attribution: '© Instituto Geográfico Nacional de España',
    kind: MapKind.TOPOGRAPHIC,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://www.ign.es/wmts/mapa-raster?service=WMTS&request=GetTile'
          + '&version=1.0.0&layer=MTN&style=default&format=image/jpeg'
          + '&tilematrixset=GoogleMapsCompatible'
          + '&TileMatrix={z}&TileRow={y}&TileCol={x}',
        maxZoom: 19,
      },
      [MapType.SATELLITE]: {
        url: 'https://www.ign.es/wmts/pnoa-ma?service=WMTS&request=GetTile'
          + '&version=1.0.0&layer=OI.OrthoimageCoverage&style=default'
          + '&format=image/jpeg&tilematrixset=GoogleMapsCompatible'
          + '&TileMatrix={z}&TileRow={y}&TileCol={x}',
        maxZoom: 20,
      },
    },
  },

  // -- France ----------------------------------------------------------
  // Licence Ouverte / Etalab 2.0.
  {
    id: MapProvider.IGN_FR,
    name: 'IGN France',
    region: 'FR',
    flag: '\u{1F1EB}\u{1F1F7}',
    attribution: '© IGN / Géoplateforme',
    kind: MapKind.STREET,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile'
          + '&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2'
          + '&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png'
          + '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
        maxZoom: 19,
      },
      [MapType.SATELLITE]: {
        url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile'
          + '&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS'
          + '&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/jpeg'
          + '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
        maxZoom: 19,
      },
    },
  },

  // -- Switzerland -----------------------------------------------------
  // swisstopo open data: free use including commercial, with attribution.
  // REST WMTS, and despite that it really does take x before y.
  {
    id: MapProvider.SWISSTOPO,
    name: 'swisstopo',
    region: 'CH',
    flag: '\u{1F1E8}\u{1F1ED}',
    attribution: '© swisstopo',
    kind: MapKind.TOPOGRAPHIC,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe'
          + '/default/current/3857/{z}/{x}/{y}.jpeg',
        maxZoom: 19,
      },
      [MapType.SATELLITE]: {
        url: 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage'
          + '/default/current/3857/{z}/{x}/{y}.jpeg',
        maxZoom: 20,
      },
    },
  },

  // -- Netherlands -----------------------------------------------------
  // PDOK open data. Also x before y, same as swisstopo.
  {
    id: MapProvider.PDOK,
    name: 'PDOK',
    region: 'NL',
    flag: '\u{1F1F3}\u{1F1F1}',
    attribution: '© PDOK / Kadaster',
    kind: MapKind.STREET,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0'
          + '/standaard/EPSG:3857/{z}/{x}/{y}.png',
        maxZoom: 20,
      },
      [MapType.SATELLITE]: {
        url: 'https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0'
          + '/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg',
        maxZoom: 21,
      },
    },
  },

  // -- Austria ---------------------------------------------------------
  // basemap.at, CC BY 4.0. Row before column.
  {
    id: MapProvider.BASEMAP_AT,
    name: 'basemap.at',
    region: 'AT',
    flag: '\u{1F1E6}\u{1F1F9}',
    attribution: '© basemap.at',
    kind: MapKind.STREET,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://mapsneu.wien.gv.at/basemap/geolandbasemap'
          + '/normal/google3857/{z}/{y}/{x}.png',
        maxZoom: 20,
      },
      [MapType.SATELLITE]: {
        url: 'https://mapsneu.wien.gv.at/basemap/bmaporthofoto30cm'
          + '/normal/google3857/{z}/{y}/{x}.jpeg',
        maxZoom: 20,
      },
    },
  },

  // -- Norway ----------------------------------------------------------
  // Kartverket, CC BY 4.0. Topographic only -- the aerial service behind
  // Norge i bilder needs a key, so SATELLITE falls back to this.
  {
    id: MapProvider.KARTVERKET,
    name: 'Kartverket',
    region: 'NO',
    flag: '\u{1F1F3}\u{1F1F4}',
    attribution: '© Kartverket',
    kind: MapKind.TOPOGRAPHIC,
    hasAerial: false,
    layers: {
      default: {
        url: 'https://cache.kartverket.no/v1/wmts/1.0.0/topo'
          + '/default/webmercator/{z}/{y}/{x}.png',
        maxZoom: 18,
      },
    },
  },

  // -- Poland ----------------------------------------------------------
  // GUGiK open data. The zoom goes into the matrix id rather than standing
  // alone, which is exactly why these are templates and not a scheme flag.
  {
    id: MapProvider.GEOPORTAL_PL,
    name: 'Geoportal',
    region: 'PL',
    flag: '\u{1F1F5}\u{1F1F1}',
    attribution: '© GUGiK',
    kind: MapKind.AERIAL,
    hasAerial: true,
    layers: {
      default: {
        url: 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMTS'
          + '/StandardResolution?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0'
          + '&LAYER=ORTOFOTOMAPA&TILEMATRIXSET=EPSG:3857'
          + '&TILEMATRIX=EPSG:3857:{z}&TILEROW={y}&TILECOL={x}'
          + '&FORMAT=image/jpeg&STYLE=default',
        maxZoom: 19,
      },
    },
  },
];

export const MAP_PROVIDER_DEF_BY_ID = new Map<string, MapProviderDef>(
  MAP_PROVIDER_DEFS.map(def => [def.id as string, def]));
